import { useRef, useEffect, useState } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDate } from '../lib/dateUtils';
import { AccountingEngine } from '../services/accountingEngine';
import { AppModal } from '../components/AppModal';

export const LedgerVouchersView = ({ config, handleItemSelect, ledgerId, selectedIndex, setSelectedIndex }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);

  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<any[] | null>(null);

  const ledgerData = useLiveQuery(async () => {
    if (!activeCompany || !ledgerId) return null;
    const lId = Number(ledgerId);
    if (isNaN(lId)) return null;

    const ledger = await db.ledgers.get(lId);
    if (!ledger) return null;

    const companyId = ledger.companyId;

    // BATCH LOAD — one query per table, no N+1 queries
    const entries = await db.voucherEntries.where('ledgerId').equals(lId).toArray();
    if (entries.length === 0) {
      const openingAmt = ledger.openingBalance || 0;
      const openingSigned = ledger.balanceType === 'Dr' ? openingAmt : -openingAmt;
      return {
        ledger,
        vouchers: [],
        openingBalance: openingAmt.toFixed(2),
        openingType: ledger.balanceType || 'Dr',
        totalDebit: '0.00',
        totalCredit: '0.00',
        closingBalance: Math.abs(openingSigned).toFixed(2),
        closingType: openingSigned >= 0 ? 'Dr' : 'Cr',
      };
    }

    const voucherIds = [...new Set(entries.map(e => e.voucherId))];
    const vouchersArr = await db.vouchers.where('id').anyOf(voucherIds).toArray();
    const voucherMap = new Map(vouchersArr.map(v => [v.id, v]));

    const allSiblingEntries = await db.voucherEntries.where('voucherId').anyOf(voucherIds).toArray();
    const siblingsByVoucher = new Map<number, typeof allSiblingEntries>();
    for (const e of allSiblingEntries) {
      if (!siblingsByVoucher.has(e.voucherId)) siblingsByVoucher.set(e.voucherId, []);
      siblingsByVoucher.get(e.voucherId)!.push(e);
    }

    const allLedgers = await db.ledgers.where('companyId').equals(companyId).toArray();
    const ledgerMapGlobal = new Map(allLedgers.map(l => [l.id, l]));

    const allGroups = await db.groups.where('companyId').equals(companyId).toArray();
    const groupMap = new Map(allGroups.map(g => [g.id, g.name]));

    const allVoucherTypes = await db.voucherTypes.where('companyId').equals(companyId).toArray();
    const voucherTypeMap = new Map(allVoucherTypes.map(vt => [vt.name, vt]));

    const CHARGE_GROUPS = new Set([
      'Duties & Taxes', 'Direct Expenses', 'Indirect Expenses',
      'Direct Income', 'Indirect Income',
    ]);

    const isCharge = (e: any) => {
      const led = ledgerMapGlobal.get(e.ledgerId);
      if (!led) return false;
      const gName = (led as any).groupId ? groupMap.get((led as any).groupId) : '';
      return gName ? CHARGE_GROUPS.has(gName) : false;
    };

    // Sort THIS ledger's entries by voucher date then voucher id
    const sortedEntries = [...entries].sort((a, b) => {
      const va = voucherMap.get(a.voucherId);
      const vb = voucherMap.get(b.voucherId);
      if (!va || !vb) return 0;
      if (va.date !== vb.date) return va.date.localeCompare(vb.date);
      return (va.id ?? 0) - (vb.id ?? 0);
    });

    // Running balance: positive = Dr, negative = Cr
    let runningBalance = ledger.balanceType === 'Dr'
      ? (ledger.openingBalance || 0)
      : -(ledger.openingBalance || 0);

    // Accumulate pre-period entries — using THIS ledger's actual Dr/Cr for each entry
    for (const entry of sortedEntries) {
      const voucher = voucherMap.get(entry.voucherId);
      if (!voucher || voucher.deleted) continue;
      if (voucher.date >= fromDate) break;
      if (entry.type === 'Dr') runningBalance += entry.amount;
      else runningBalance -= entry.amount;
    }

    const openingBalanceSigned = runningBalance;
    const openingBalanceAmt = Math.abs(openingBalanceSigned).toFixed(2);
    const openingBalanceType: 'Dr' | 'Cr' = openingBalanceSigned >= 0 ? 'Dr' : 'Cr';

    const result: any[] = [];

    for (const entry of sortedEntries) {
      const voucher = voucherMap.get(entry.voucherId);
      if (!voucher || voucher.deleted) continue;
      if (voucher.date < fromDate || voucher.date > toDate) continue;

      // Particulars = the OTHER side of this voucher entry
      const allVoucherEntries = siblingsByVoucher.get(voucher.id!) || [];
      let particulars = 'Unknown';

      if (allVoucherEntries.length > 0) {
        const voucherTypeRecord = voucherTypeMap.get(voucher.type);

        let partyDrCr: 'Dr' | 'Cr' | null = null;
        if (voucherTypeRecord?.stockDirection === 'Out') partyDrCr = 'Dr';
        else if (voucherTypeRecord?.stockDirection === 'In') partyDrCr = 'Cr';
        else if (['Sales', 'Credit Note'].includes(voucher.type)) partyDrCr = 'Dr';
        else if (['Purchase', 'Debit Note'].includes(voucher.type)) partyDrCr = 'Cr';
        else if (voucher.type === 'Receipt') partyDrCr = 'Cr';
        else if (voucher.type === 'Payment') partyDrCr = 'Dr';

        let targetEntry = allVoucherEntries.find((e: any) => e.id !== entry.id);
        if (partyDrCr) {
          targetEntry =
            allVoucherEntries.find((e: any) => e.type === partyDrCr && !isCharge(e) && e.id !== entry.id) ||
            allVoucherEntries.find((e: any) => e.type === partyDrCr && e.id !== entry.id) ||
            targetEntry;
        }

        if (targetEntry) {
          const led = ledgerMapGlobal.get((targetEntry as any).ledgerId);
          particulars = (led as any)?.name || 'Unknown';
        }
      }

      const isRemoved = removedIds.has(voucher.id!);
      if (!isRemoved) {
        if (entry.type === 'Dr') runningBalance += entry.amount;
        else runningBalance -= entry.amount;
      }

      result.push({
        id: voucher.id,
        date: voucher.date,
        particulars,
        vchType: voucher.type,
        vchNo: voucher.voucherNumber || '',
        refNo: voucher.referenceNumber || '',
        debit: entry.type === 'Dr' ? entry.amount.toFixed(2) : '',
        credit: entry.type === 'Cr' ? entry.amount.toFixed(2) : '',
        balance: Math.abs(runningBalance).toFixed(2),
        balanceType: runningBalance >= 0 ? 'Dr' : 'Cr',
        name: particulars,
        isRemoved,
        billAllocations: (voucher as any).billAllocations || [],
      });
    }

    // Compute column totals for "Current Total" row
    const totalDebit  = result.filter(r => !r.isRemoved && r.debit).reduce((s, r) => s + parseFloat(r.debit), 0);
    const totalCredit = result.filter(r => !r.isRemoved && r.credit).reduce((s, r) => s + parseFloat(r.credit), 0);

    return {
      ledger,
      vouchers: result,
      openingBalance: openingBalanceAmt,
      openingType: openingBalanceType,
      totalDebit: totalDebit.toFixed(2),
      totalCredit: totalCredit.toFixed(2),
      closingBalance: Math.abs(runningBalance).toFixed(2),
      closingType: runningBalance >= 0 ? 'Dr' : 'Cr',
    };
  }, [activeCompany, ledgerId, fromDate, toDate, removedIds]);

  useWheelNavigation(scrollRef, ledgerData?.vouchers.length || 0, setSelectedIndex, false, () => {
    const v = ledgerData?.vouchers?.[selectedIndex];
    if (v && !v.isRemoved) handleItemSelect(v, selectedIndex);
  });

  const processAction = async (actionKey: string) => {
    const flatItems = ledgerData?.vouchers || [];
    const targetItems = selectedIds.size > 0
      ? flatItems.filter((item: any) => selectedIds.has(item.id))
      : (flatItems[selectedIndex] ? [flatItems[selectedIndex]] : []);

    if (actionKey === 'Space' || actionKey === ' ') {
      const item = flatItems[selectedIndex];
      if (item) {
        setSelectedIds(prev => {
          const next = new Set(prev);
          if (next.has(item.id)) next.delete(item.id);
          else next.add(item.id);
          return next;
        });
      }
    } else if (actionKey.toLowerCase() === 'a') {
      handleItemSelect({ name: 'Vouchers' });
    } else if (actionKey.toLowerCase() === 'd') {
      if (targetItems.length === 0) return;
      setPendingDelete(targetItems);
    } else if (actionKey.toLowerCase() === 'r') {
      setRemovedIds(prev => {
        const next = new Set(prev);
        for (const item of targetItems) next.add(item.id);
        return next;
      });
      setSelectedIds(new Set());
      setSelectedIndex(Math.min(flatItems.length - 1, selectedIndex + 1));
    } else if (actionKey.toLowerCase() === 'u') {
      setRemovedIds(prev => {
        const next = new Set(prev);
        if (selectedIds.size > 0) {
          for (const item of targetItems) next.delete(item.id);
        } else {
          const item = flatItems[selectedIndex];
          if (item) next.delete(item.id);
        }
        return next;
      });
      setSelectedIds(new Set());
    } else if (actionKey === 'Enter') {
      const item = flatItems[selectedIndex];
      if (item && !item.isRemoved) handleItemSelect(item, selectedIndex);
    } else if (actionKey === 'F2') {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'F2', altKey: true }));
    } else if (actionKey === 'F4') {
      handleItemSelect({ name: 'F4: Ledger' });
    } else if (actionKey === '2') {
      const item = flatItems[selectedIndex];
      if (item && !item.isRemoved) {
        handleItemSelect({ name: 'Duplicate Voucher', level: 'duplicate_voucher_form', id: item.id });
      }
    } else if (actionKey.toLowerCase() === 'q' || actionKey === 'Escape') {
      handleItemSelect({ name: 'Quit' });
    }
  };

  const executeDelete = async () => {
    const targetItems = pendingDelete || [];
    const flatItems = ledgerData?.vouchers || [];
    setPendingDelete(null);
    try {
      let maxAmt = 0;
      for (const v of targetItems) {
        await AccountingEngine.deleteVoucher(v.id, activeCompany!.id!);
        const amt = v.debit ? parseFloat(v.debit) : (v.credit ? parseFloat(v.credit) : 0);
        if (!isNaN(amt) && amt > maxAmt) maxAmt = amt;
      }
      let msg = targetItems.length > 1
        ? `Moved ${targetItems.length} vouchers to Bin Box.`
        : `Moved voucher ${targetItems[0].vchNo || ''} to Bin Box.`;
      if (maxAmt > 50000) msg += ` (High Value: ${maxAmt.toFixed(2)})`;
      useStore.getState().setGlobalMessage(msg);
      setSelectedIds(new Set());
      setSelectedIndex(Math.max(0, Math.min(flatItems.length - targetItems.length - 1, selectedIndex)));
    } catch (err: any) {
      alert(`Failed to soft-delete voucher: ${err.message}`);
    }
  };

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (pendingDelete) return;
      const viewKeys = [' ', 'Enter', 'Escape', 'F2', 'F4', 'a', 'd', 'r', 'u', 'q', '2'];
      if (viewKeys.includes(e.key) || viewKeys.includes(e.key.toLowerCase())) {
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.preventDefault();
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.stopPropagation();
      }
      await processAction(e.key);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ledgerData, selectedIndex, activeCompany, handleItemSelect, selectedIds, removedIds, setSelectedIndex, pendingDelete]);

  const shortcuts = [
    { key: 'Q', label: 'Quit' }, { key: 'Enter', label: 'Alter' }, { key: 'Space', label: 'Select' },
    { key: 'A', label: 'Add Vch' }, { key: '2', label: 'Duplicate Vch' }, { key: 'D', label: 'Delete' },
    { key: 'R', label: 'Remove Line' }, { key: 'U', label: 'Restore Line' },
    { key: 'F2', label: 'Period' }, { key: 'F4', label: 'Ledger' },
  ];

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px]">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Ledger Vouchers</span>
        <span className="w-1/3 text-center">{activeCompany?.name || config?.companyList?.[0]?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>x</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span className="text-[#1e8585]">{ledgerData?.ledger?.name || 'Loading...'}</span>
        <span>{formatDate(fromDate)} to {formatDate(toDate)}</span>
      </div>

      <div className="flex flex-col border-b border-[#aebec8] bg-white text-black font-bold px-2 py-1 shadow-sm">
        <div className="flex w-full">
          <div className="w-[80px]">Date</div>
          <div className="flex-1">Particulars</div>
          <div className="w-[120px]">Vch Type</div>
          <div className="w-[80px] text-right">Vch No.</div>
          <div className="w-[100px] text-right">Debit</div>
          <div className="w-[100px] text-right">Credit</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white scrollbar-thin">
        {ledgerData && (
          <div className="flex w-full px-2 py-[2px] bg-[#f0f9f9] text-[#1e8585] font-bold border-b border-[#d0e8e8]">
            <div className="w-[80px]">Opn Bal</div>
            <div className="flex-1 italic">Opening Balance</div>
            <div className="w-[120px]"></div>
            <div className="w-[80px]"></div>
            <div className="w-[100px] text-right">
              {ledgerData.openingType === 'Dr' && parseFloat(ledgerData.openingBalance) > 0 ? ledgerData.openingBalance : ''}
            </div>
            <div className="w-[100px] text-right">
              {ledgerData.openingType === 'Cr' && parseFloat(ledgerData.openingBalance) > 0 ? ledgerData.openingBalance : ''}
            </div>

          </div>
        )}

        {ledgerData?.vouchers.map((item: any, idx: number) => {
          const isSelectedRow = selectedIndex === idx;
          const isChecked = selectedIds.has(item.id);

          let rowClass = 'text-black';
          if (isChecked) {
            rowClass = isSelectedRow
              ? 'bg-[#1b7070] text-[#ffcc00] font-bold active-row'
              : 'bg-[#1e8585] text-white font-semibold';
          } else if (isSelectedRow) {
            rowClass = 'bg-[#fff9c4] text-black font-semibold active-row';
          }
          if (item.isRemoved) rowClass += ' opacity-40 italic line-through text-gray-500';

          return (
            <div
              key={idx}
              onClick={() => setSelectedIndex(idx)}
              onDoubleClick={() => { setSelectedIndex(idx); if (!item.isRemoved) handleItemSelect(item, idx); }}
              className={`flex w-full px-2 py-[2px] cursor-pointer ${rowClass}`}
            >
              <div className="w-[80px]">{formatDate(item.date)}</div>
              <div className="flex-1 italic font-semibold flex items-center gap-1">
                {item.particulars}
                {item.billAllocations?.length > 0 && (
                  <span className="text-[9px] px-1 py-[1px] rounded bg-teal-50 text-teal-700 border border-teal-200 font-bold ml-1 non-italic" title={item.billAllocations.filter((a: any) => a.type === 'against_ref').map((a: any) => `${a.billNo}: ₹${a.allocAmount}`).join(', ')}>
                    ✓ Bill-linked
                  </span>
                )}
                {item.billAllocations?.some((a: any) => a.type === 'on_account') && (
                  <span className="text-[9px] px-1 py-[1px] rounded bg-amber-50 text-amber-700 border border-amber-200 font-bold ml-1">On Acct</span>
                )}
              </div>
              <div className="w-[120px] font-semibold">{item.vchType}</div>
              <div className="w-[80px] text-right">{item.vchNo}</div>
              <div className="w-[100px] text-right font-semibold">{item.debit}</div>
              <div className="w-[100px] text-right font-semibold">{item.credit}</div>
            </div>
          );
        })}

        {ledgerData?.vouchers.length === 0 && (
          <div className="p-4 text-center text-gray-500 italic">No vouchers found for this ledger in the selected period.</div>
        )}
      </div>

      {/* Aarthika-style 3-row footer: Opening Balance / Current Total / Closing Balance */}
      <div className="border-t-2 border-[#1e8585] bg-[#f0f9f9] font-bold text-black text-[12px]">
        {/* Row 1: Opening Balance */}
        <div className="flex w-full px-2 py-[2px] border-b border-[#d0e8e8]">
          <div className="flex-1 text-left pl-2">Opening Balance :</div>
          <div className="w-[120px]"></div>
          <div className="w-[80px]"></div>
          <div className="w-[100px] text-right">
            {ledgerData?.openingType === 'Dr' && parseFloat(ledgerData?.openingBalance || '0') > 0
              ? ledgerData.openingBalance : ''}
          </div>
          <div className="w-[100px] text-right">
            {ledgerData?.openingType === 'Cr' && parseFloat(ledgerData?.openingBalance || '0') > 0
              ? ledgerData.openingBalance : ''}
          </div>
        </div>
        {/* Row 2: Current Total */}
        <div className="flex w-full px-2 py-[2px] border-b border-[#d0e8e8]">
          <div className="flex-1 text-left pl-4">Current Total :</div>
          <div className="w-[120px]"></div>
          <div className="w-[80px]"></div>
          <div className="w-[100px] text-right border-t border-black">
            {ledgerData?.totalDebit !== '0.00' ? ledgerData?.totalDebit : ''}
          </div>
          <div className="w-[100px] text-right border-t border-black">
            {ledgerData?.totalCredit !== '0.00' ? ledgerData?.totalCredit : ''}
          </div>
        </div>
        {/* Row 3: Closing Balance */}
        <div className="flex w-full px-2 py-[2px]">
          <div className="flex-1 text-left pl-2 text-[#1e8585]">Closing Balance :</div>
          <div className="w-[120px]"></div>
          <div className="w-[80px]"></div>
          <div className="w-[100px] text-right text-[#1e8585]">
            {ledgerData?.closingType === 'Dr' ? ledgerData?.closingBalance : ''}
          </div>
          <div className="w-[100px] text-right text-[#1e8585]">
            {ledgerData?.closingType === 'Cr' ? ledgerData?.closingBalance : ''}
          </div>
        </div>
      </div>

      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner mt-auto">
        {shortcuts.map((sc, i) => (
          <div
            key={i}
            className="flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100"
            onClick={() => processAction(sc.key)}
          >
            <div className="flex items-center space-x-[2px] whitespace-nowrap">
              <span className={`text-[#2a62a3] font-bold ${sc.key.length === 1 ? 'underline decoration-[#2a62a3] underline-offset-2' : ''}`}>
                {sc.key[0]}
              </span>
              {sc.key.length > 1 && <span className="text-[#2a62a3] font-bold">{sc.key.substring(1)}</span>}
              <span className="text-[#2a62a3] font-bold">:</span>
              <span className="text-black leading-none">{sc.label}</span>
            </div>
            <span className="text-[#aebec8] text-[9px] mb-1">^</span>
          </div>
        ))}
      </div>

      <AppModal
        open={!!pendingDelete}
        title="Move to Bin Box"
        type="warning"
        message={
          pendingDelete && pendingDelete.length > 1
            ? `Move ${pendingDelete.length} vouchers to Bin Box?`
            : `Move voucher ${pendingDelete?.[0]?.vchNo || ''} to Bin Box?`
        }
        confirmLabel="Move to Bin Box"
        cancelLabel="Cancel"
        onConfirm={executeDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};
